// lib/helpers/intercom.js
import { hide, onHide, onShow, show, update } from '@intercom/messenger-js-sdk';

import { pushSessionReplayLinkToIntercom } from 'lib/helpers/intercomSessionReplay';

export const hideDefaultIntercomLauncher = () => {
  update({
    hide_default_launcher: true,
    custom_launcher_selector: '[data-intercom-trigger="true"]'
  });
};

export const waitForMessengerAndPosition = (messengerPosition = 'left') => {
  const maxAttempts = 10;
  let attempts = 0;

  const interval = setInterval(() => {
    const frame = document.querySelector('.intercom-messenger-frame');
    if (frame) {
      positionIntercomMessenger(messengerPosition);
      clearInterval(interval);
    }

    if (++attempts > maxAttempts) {
      clearInterval(interval);
    }
  }, 100);
};

export const injectIntercomStyles = () => {
  const style = document.createElement('style');
  style.textContent = `
      .intercom-lightweight-app { display: none !important; visibility: hidden !important; }
      #intercom-container .intercom-launcher-frame { display: none !important; visibility: hidden !important; }
      #intercom-container div[class*="intercom-launcher"] { display: none !important; visibility: hidden !important; }
    `;
  document.head.appendChild(style);
  return style;
};

// Constants for messenger positioning
const MESSENGER_CONFIG = {
  width: 376,
  height: 704,
  spacing: 15,
  mobileBreakpoint: 480
};

const getPositioningContext = (button) => {
  const buttonRect = button.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  return {
    button: buttonRect,
    viewport: { width: viewportWidth, height: viewportHeight },
    isMobile: viewportWidth < MESSENGER_CONFIG.mobileBreakpoint
  };
};

const calculateInitialPosition = (position, context) => {
  const { button } = context;
  const { width: messengerWidth, height: messengerHeight, spacing } = MESSENGER_CONFIG;

  const positions = {
    right: {
      left: button.right + spacing,
      top: button.top
    },
    top: {
      left: button.left,
      top: button.top - messengerHeight - spacing
    },
    bottom: {
      left: button.left,
      top: button.bottom + spacing
    },
    left: {
      left: button.left - messengerWidth - spacing,
      top: button.top
    }
  };

  return positions[position] || positions.left;
};

const adjustPositionForOverflow = (coords, position, context) => {
  const { button, viewport } = context;
  const { width: messengerWidth, height: messengerHeight, spacing } = MESSENGER_CONFIG;
  let { top, left } = coords;

  // Handle horizontal overflow with flipping
  if (position === 'right' && left + messengerWidth > viewport.width) {
    left = button.left - messengerWidth - spacing; // Flip to left
  } else if (position === 'left' && left < spacing) {
    left = button.right + spacing; // Flip to right
  }

  // Handle vertical overflow with flipping
  if (position === 'top' && top < spacing) {
    top = button.bottom + spacing; // Flip to bottom
  } else if (position === 'bottom' && top + messengerHeight > viewport.height) {
    top = button.top - messengerHeight - spacing; // Flip to top
  }

  // Handle edge cases for horizontal positions
  if ((position === 'left' || position === 'right') && top + messengerHeight > viewport.height) {
    top = viewport.height - messengerHeight - spacing;
  }

  // Handle edge cases for vertical positions
  if ((position === 'top' || position === 'bottom') && left + messengerWidth > viewport.width) {
    left = viewport.width - messengerWidth - spacing;
  }

  return { top, left };
};

const clampToViewport = (coords, viewport) => {
  const { width: messengerWidth, height: messengerHeight, spacing } = MESSENGER_CONFIG;

  return {
    left: Math.max(spacing, Math.min(coords.left, viewport.width - messengerWidth - spacing)),
    top: Math.max(spacing, Math.min(coords.top, viewport.height - messengerHeight - spacing))
  };
};

const applyMessengerStyles = (messenger, coords) => {
  Object.assign(messenger.style, {
    top: `${coords.top}px`,
    left: `${coords.left}px`,
    right: 'auto',
    bottom: 'auto',
    position: 'fixed',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
  });
};

const hideDefaultLaunchers = () => {
  const defaultLaunchers = document.querySelectorAll(
    '#intercom-container .intercom-launcher-frame'
  );
  defaultLaunchers.forEach((launcher) => {
    Object.assign(launcher.style, {
      display: 'none',
      visibility: 'hidden'
    });
  });
};

export const positionIntercomMessenger = (position = 'left') => {
  const customButton = document.querySelector('[data-intercom-trigger="true"]');
  if (!customButton) return;

  const intercomMessenger = document.querySelector('.intercom-messenger-frame');
  if (!intercomMessenger) return;

  const context = getPositioningContext(customButton);

  // Skip positioning on mobile - let Intercom handle it
  if (context.isMobile) {
    hideDefaultLaunchers();
    return;
  }

  // Calculate and adjust position
  const initialCoords = calculateInitialPosition(position, context);
  const adjustedCoords = adjustPositionForOverflow(initialCoords, position, context);
  const finalCoords = clampToViewport(adjustedCoords, context.viewport);

  // Apply styles and hide default launchers
  applyMessengerStyles(intercomMessenger, finalCoords);
  hideDefaultLaunchers();
};

export const resetMessengerPosition = () => {
  const intercomMessenger = document.querySelector('.intercom-messenger-frame');
  if (intercomMessenger) {
    intercomMessenger.style.top = '';
    intercomMessenger.style.left = '';
    intercomMessenger.style.right = '';
    intercomMessenger.style.bottom = '';
    intercomMessenger.style.position = '';
    intercomMessenger.style.animation = '';
    intercomMessenger.style.transform = '';
    intercomMessenger.style.boxShadow = '';
    intercomMessenger.style.transition = '';
  }
};

export const setupIntercomEventHandlers = (setIsOpen, ignoreResets, messengerPosition = 'left') => {
  onHide(() => {
    setIsOpen(false);
    ignoreResets.current = true;
  });

  onShow(() => {
    setIsOpen(true);
    ignoreResets.current = false;

    // keep the Datadog replay link fresh right before the user can start a conversation
    pushSessionReplayLinkToIntercom();
    positionIntercomMessenger(messengerPosition);
  });
};

export const initializeIntercom = (callback) => {
  hideDefaultIntercomLauncher();

  if (callback) callback();
};

// Re-export SDK functions for convenience
export { hide, show };
